import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GoogleIcon from "@mui/icons-material/Google";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import EmailIcon from "@mui/icons-material/Email";
import KeyIcon from "@mui/icons-material/Key";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import { useAuthData } from "modules/Authentication/hooks/useAuthData";
import { ILoginFormType } from "types";

type Props = {};

// Mock function to simulate email check
const isEmailRegistered = (email: string) => {
  const registeredEmails = ["test@example.com", "user@example.com"]; // Mock list of registered emails
  return registeredEmails.includes(email);
};
export const RegisterForm = (props: Props) => {
  const {
    onRegister,
    loading,
    error,
    setError,
    loginWithGoogle,
    loginWithFacebook,
    loginWithTwitter,
  } = useAuthData();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isConfrimPasswordValid, setIsConfrimPasswordValid] = useState(false);
  const [registerError, setRegisterError] = useState<string[]>([]);
  const [isAgreed, setIsAgreed] = useState(false);
  const [emailAlreadyRegisteredError, setEmailAlreadyRegisteredError] =
    useState("");

  const togglePasswordVisibility = () => {
    // setShowPassword(!showPassword);
    setShowPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const validatePasswords = (password: string, confirmPassword: string) => {
    let validPassword = true;
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push("Minimum 8 characters");
      validPassword = false;
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("At least 1 uppercase letter");
      validPassword = false;
    }
    if (!/[a-z]/.test(password)) {
      errors.push("At least 1 lowercase letter");
      validPassword = false;
    }
    if (!/[0-9]/.test(password)) {
      errors.push("At least 1 number");
      validPassword = false;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push("At least 1 special symbol");
      validPassword = false;
    }

    // setIsPasswordValid(validPassword);
    setPasswordError(errors.length > 0 ? errors.join(", ") : "");

    setRegisterError(errors);
    setIsPasswordValid(validPassword);

    if (!confirmPassword) {
      // errors.push("Confirm password is required");
      validPassword = false;
      return;
    }

    if (password !== confirmPassword) {
      errors.push("Passwords won’t match");
      validPassword = false;
    }
    setIsConfrimPasswordValid(validPassword);

    if (password === confirmPassword) {
      setConfirmPasswordError("");
    } else {
      setConfirmPasswordError("");
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === "email") {
      setEmail(value);
      setError(null);
      validateEmail(value);
    } else if (name === "password") {
      setPassword(value);
      validatePasswords(value, confirmPassword);
    } else if (name === "confirmPassword") {
      setConfirmPassword(value);
      validatePasswords(password, value);
    }
  };

  const validateEmail = (value: string): void => {
    let errors: string[] = [];

    if (!value.trim()) {
      errors.push("Email is required");
      setIsEmailValid(false);
    } else {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!regex.test(value)) {
        errors.push("Invalid email format");
        setIsEmailValid(false);
      } else {
        setIsEmailValid(true);
      }
    }

    // Check if email is already registered
    if (isEmailRegistered(value)) {
      errors.push("This email is already registered.");
    }

    setEmailError(errors.length > 0 ? errors[0] : ""); // Set error for text field
    setRegisterError(errors); // Update register error
  };

  const handleRegister = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate the email
    validateEmail(email);

    if (!email || !isEmailValid) return; // Stop if email is invalid

    // Check if the email is already registered
    if (isEmailRegistered(email)) {
      // setEmailAlreadyRegisteredError("This email is already registered.");
      setEmailError("This Email address is already taken");
      return;
    }

    // Validate the password and confirm password match
    if (password !== confirmPassword) {
      setConfirmPasswordError("");
      return; // Stop the registration process if passwords do not match
    }

    validatePasswords(password, confirmPassword);

    const loginData: ILoginFormType = {
      email: email,
      password: password,
      isKeepLoggedIn: true,
      isAdmin: false,
    };

    try {
      // Attempt to register
      const user = await onRegister(loginData);
      if (user) {
        // If registration is successful, navigate to the dashboard
        navigate("/login");
      }
    } catch (error: any) {
      if (error.message === "auth/email-already-in-use") {
        // If the email is already registered, set the error message
        setRegisterError(["This Email address is already taken"]);
        // setEmailAlreadyRegisteredError("This Email address is already taken");
        // If the email is already registered, prompt the user to log in
        // alert(
        //   "This email is already registered. You are being redirected to the login page."
        // );

        // Optionally, auto-login the user or redirect them to the login page
        // navigate("/login");
      } else {
        // Handle other errors
        console.error("Registration failed:", error);
      }
    }
  };

  const handleNavigateBack = () => {
    navigate(-1);
  };

  // social icons ///
  const handleSocialLogin = async (providerLoginFn: Function) => {
    try {
      const user = await providerLoginFn();
      console.log("RegisterForm---->", user);
      if (user) {
        navigate("/"); // Redirect on successful login
      }
    } catch (error) {
      console.error("Social login failed", error);
    }
  };

  return (
    <React.Fragment>
      <form onSubmit={handleRegister}>
        <Grid item onClick={handleNavigateBack}>
          <ArrowBackIcon sx={{ cursor: "pointer" }} />
        </Grid>
        <Grid mt={3} container spacing={2} direction="column">
          <Grid item>
            <Box
              fontSize={{ xs: "20.76px", md: "28px" }}
              fontWeight={{ xs: "600", md: "500" }}
              color="black"
            >
              Register
            </Box>
            <Typography
              fontSize={{ xs: "15px", md: "18px" }}
              fontWeight={{ xs: "400", md: "400" }}
              color="#6D6D6D"
              display="flex"
              gap={1}
            >
              Enter your information to create an account.
            </Typography>
          </Grid>
          <Grid item xs={12} mt={2}>
            <TextField
              type="email"
              name="email"
              variant="outlined"
              fullWidth
              placeholder="Enter your email"
              error={!!emailError || !!emailAlreadyRegisteredError}
              // helperText={emailError || emailAlreadyRegisteredError}
              value={email}
              onChange={(e: any) => {
                setEmailAlreadyRegisteredError("");
                handleChange(e);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon
                      sx={{
                        color: "#6D6D6D",
                        width: { xs: "11.86px", md: "20px" },
                        height: { xs: "12px", md: "20px" },
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {email && (
                      <Typography
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Box
                          component="span"
                          sx={{ color: isEmailValid ? "#808080" : "red" }}
                        >
                          {isEmailValid ? (
                            <CheckIcon
                              sx={{
                                color: "green",
                                width: { xs: "11.86px", md: "20px" },
                                height: { xs: "12px", md: "20px" },
                              }}
                            />
                          ) : (
                            <CloseIcon
                              sx={{
                                color: "red",
                                width: { xs: "11.86px", md: "20px" },
                                height: { xs: "12px", md: "20px" },
                              }}
                            />
                          )}
                        </Box>
                      </Typography>
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: "white",
                borderRadius: "12px",
                "& .MuiInputBase-root": {
                  height: { xs: "35.39px", md: "48px" },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#6D6D6D",
                  fontSize: { xs: "12px", md: "16px" },
                  fontWeight: "500",
                  opacity: 1,
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderRadius: { xs: "8.9px", md: "12px" },
                    borderColor: passwordError.length
                      ? "red"
                      : "#E7E7E7 !important", // This ensures the border is red only if there's an error
                  },
                  "&:hover fieldset": {
                    borderColor: passwordError.length ? "red" : "#E7E7E7",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: passwordError.length ? "red" : "#E7E7E7",
                  },
                  "& input": {
                    color: passwordError.length ? "red" : "inherit",
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} display={"flex"} direction={"column"}>
            <Grid item xs={12}>
              <TextField
                type={showPassword ? "text" : "password"}
                name="password"
                variant="outlined"
                fullWidth
                placeholder="Enter your password"
                error={!!passwordError}
                value={password}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        sx={{
                          width: { xs: "11.86px", md: "20px" },
                          height: { xs: "16px", md: "20px" }, // you can adjust height as needed
                          color: "#6D6D6D",
                        }}
                      >
                        <svg
                          style={{
                            width: "100%",
                            height: "100%",
                          }}
                          color="#6D6D6D"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            opacity="0.7"
                            d="M8.8125 10.75L7.75 11.7812C7.625 11.9375 7.4375 12 7.25 12H6V13.25C6 13.6875 5.65625 14 5.25 14H4V15.25C4 15.6875 3.65625 16 3.25 16H0.75C0.3125 16 0 15.6875 0 15.25V12.75C0 12.5625 0.0625 12.375 0.21875 12.2188L5.25 7.1875C5.0625 6.65625 5 6.09375 5 5.5C5 2.46875 7.4375 0 10.5 0C13.5312 0 16 2.46875 16 5.5C16 8.5625 13.5312 11 10.5 11C9.90625 11 9.34375 10.9375 8.8125 10.75ZM11.75 5.5C12.4375 5.5 13 4.96875 13 4.25C13 3.5625 12.4375 3 11.75 3C11.0312 3 10.5 3.5625 10.5 4.25C10.5 4.96875 11.0312 5.5 11.75 5.5Z"
                            fill="#6D6D6D"
                          />
                        </svg>
                      </Box>
                    </InputAdornment>
                  ),

                  endAdornment: (
                    <InputAdornment position="end">
                      {password && (
                        <Typography
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <Box
                            component="span"
                            sx={{ color: isPasswordValid ? "#808080" : "red" }}
                          >
                            {isPasswordValid ? (
                              <CheckIcon
                                sx={{
                                  color: "green",
                                  width: { xs: "11.86px", md: "20px" },
                                  height: { xs: "12px", md: "20px" },
                                }}
                              />
                            ) : (
                              <CloseIcon
                                sx={{
                                  color: "red",
                                  width: { xs: "11.86px", md: "20px" },
                                  height: { xs: "12px", md: "20px" },
                                }}
                              />
                            )}
                          </Box>
                        </Typography>
                      )}
                      <Typography
                        onClick={togglePasswordVisibility}
                        sx={{ cursor: "pointer" }}
                      >
                        <Box component="span" sx={{ color: "#808080" }}>
                          {showPassword ? (
                            <VisibilityIcon
                              sx={{
                                width: { xs: "15px", md: "20px" },
                                height: { xs: "12px", md: "20px" },
                              }}
                            />
                          ) : (
                            <VisibilityOffIcon
                              sx={{
                                width: { xs: "15px", md: "20px" },
                                height: { xs: "12px", md: "20px" },
                              }}
                            />
                          )}
                        </Box>
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: { xs: "35.39px", md: "48px" },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#6D6D6D",
                    fontSize: { xs: "12px", md: "16px" },
                    fontWeight: "500",
                    opacity: 1,
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderTopLeftRadius: "12px",
                      borderTopRightRadius: "12px",
                      borderBottomRightRadius: "0px",
                      borderBottomLeftRadius: "0px",
                      borderColor: passwordError.length
                        ? "red"
                        : "#E7E7E7 !important", // This ensures the border is red only if there's an error
                    },
                    "&:hover fieldset": {
                      borderColor: passwordError.length ? "red" : "#E7E7E7",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: passwordError.length ? "red" : "#E7E7E7",
                    },
                    "& input": {
                      color: passwordError.length ? "red" : "inherit",
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                variant="outlined"
                fullWidth
                placeholder="Re-enter your password"
                error={!!confirmPasswordError}
                helperText={confirmPasswordError}
                value={confirmPassword}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {confirmPassword && (
                        <Typography
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <Box
                            component="span"
                            sx={{
                              color: isConfrimPasswordValid ? "#808080" : "red",
                            }}
                          >
                            {isConfrimPasswordValid ? (
                              <CheckIcon
                                sx={{
                                  color: "green",
                                  width: { xs: "11.86px", md: "20px" },
                                  height: { xs: "12px", md: "20px" },
                                }}
                              />
                            ) : (
                              <CloseIcon
                                sx={{
                                  color: "red",
                                  width: { xs: "11.86px", md: "20px" },
                                  height: { xs: "12px", md: "20px" },
                                }}
                              />
                            )}
                          </Box>
                        </Typography>
                      )}
                      <Typography
                        onClick={toggleConfirmPasswordVisibility}
                        sx={{ cursor: "pointer" }}
                      >
                        <Box component="span" sx={{ color: "#808080" }}>
                          {showConfirmPassword ? (
                            <VisibilityIcon
                              sx={{
                                width: { xs: "15px", md: "20px" },
                                height: { xs: "12px", md: "20px" },
                              }}
                            />
                          ) : (
                            <VisibilityOffIcon
                              sx={{
                                width: { xs: "15px", md: "20px" },
                                height: { xs: "12px", md: "20px" },
                              }}
                            />
                          )}
                        </Box>
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: { xs: "35.39px", md: "48px" },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#6D6D6D",
                    fontSize: { xs: "12px", md: "16px" },
                    fontWeight: "500",
                    opacity: 1,
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderBottomLeftRadius: "12px",
                      borderBottomRightRadius: "12px",
                      borderTopLeftRadius: "0px",
                      borderTopRightRadius: "0px",
                      borderTopColor: "#fff",
                      borderColor: passwordError.length
                        ? "red"
                        : "#E7E7E7 !important", // This ensures the border is red only if there's an error
                    },
                    "&:hover fieldset": {
                      borderColor: passwordError.length ? "red" : "#E7E7E7",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: passwordError.length ? "red" : "#E7E7E7",
                    },
                    "& input": {
                      color: passwordError.length ? "red" : "inherit",
                    },
                    ".css-nxo287-MuiInputBase-input-MuiOutlinedInput-input": {
                      padding: {
                        xs: "16.5px 34px !important",
                        md: "16.5px 42px !important",
                      },
                    },
                  },
                }}
              />
            </Grid>
          </Grid>

          <Grid item xs={12} display={"flex"} alignItems={"center"} gap={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAgreed}
                  onChange={(e) => setIsAgreed(e.target.checked)}
                  sx={{
                    "&.Mui-checked": {
                      color: "#2C2C2C",
                    },
                  }}
                />
              }
              label="I agree with the "
              sx={{
                marginRight: "0px",
                color: "#6D6D6D",
                fontSize: "16px",
                fontWeight: "500",
              }}
            />
            <Link to="/legalities-policy" style={styles.link2}>
              Legalities and Policies
            </Link>
          </Grid>
          <Grid item>
            {registerError.length > 0 && (
              <Typography
                sx={{
                  color: "red",
                  fontSize: { xs: "10.38px", md: "14px" },
                  fontWeight: "400",
                }}
              >
                {registerError.map((errors, index) => (
                  <Typography
                    key={index}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <ErrorIcon sx={{ width: "13px", height: "13px", mr: 1 }} />
                    {errors}
                  </Typography>
                ))}
              </Typography>
            )}
            <Typography
              sx={{
                color: "red",
                fontSize: { xs: "10.38px", md: "14px" },
                fontWeight: "400",
              }}
            >
              <Typography style={{ display: "flex", alignItems: "center" }}>
                {error && (
                  <ErrorIcon sx={{ width: "13px", height: "13px", mr: 1 }} />
                )}
                {error}
              </Typography>
            </Typography>
          </Grid>

          <Grid item xs={12} mt={3}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              disabled={!isAgreed}
              sx={{
                backgroundColor: isAgreed ? "#233F91" : "#cccccc",
                borderRadius: { xs: "10px", md: "25px" },
                color: "white",
                height: "48px",
                "&:hover": {
                  backgroundColor: "#EEEEEE",
                  color: "#233F91",
                  border: "1.5px solid #233F91",
                },
              }}
            >
              Register
            </Button>
          </Grid>
          <Grid
            item
            xs={12}
            mt={2}
            mb={5}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Typography>or</Typography>
            <Grid item mt={2} display="flex" gap={1} alignItems="center">
              <Box
                sx={styles.social}
                onClick={() => handleSocialLogin(loginWithGoogle)}
              >
                <img src="/assets/icons/google.svg" alt="google" />
              </Box>
              {/* <Box
                sx={styles.social}
                onClick={() => handleSocialLogin(loginWithFacebook)}
              >
                <img src="/assets/icons/facebook.svg" alt="facebook" />
              </Box>
              <Box
                sx={styles.social}
                onClick={() => handleSocialLogin(loginWithTwitter)}
              >
                <img src="/assets/icons/twitter.svg" alt="twitter" />
              </Box> */}
            </Grid>
          </Grid>
        </Grid>
      </form>
    </React.Fragment>
  );
};

const styles = {
  link: {
    color: "black",
    textDecoration: "underline",
    fontWeight: "600",
    fontSize: "16px",
  },
  link2: {
    color: "#6D6D6D",
    textDecoration: "none",
    fontWeight: "700",
    marginRight: "2px",
    fontSize: "16px",
  },
  checkBox: {
    color: "#2C2C2C",
  },
  social: {
    display: "flex",
    p: "9px 20px 10px 20px",
    borderRadius: "8px",
    border: "1px solid #E1E1E1",
    boxShadow: 2,
    cursor: "pointer",
  },
};

export default RegisterForm;
